import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDraggable } from 'react-use-draggable-scroll';

import * as S from './style';

/** react-use-draggable-scroll safeDisplacement 기본값과 맞춤 */
const DRAG_THRESHOLD_PX = 10;

interface IProps {
  /** children */
  children: React.ReactNode;
  /** className */
  className?: string;
}

const DraggableScroller: React.FC<IProps> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const { events } = useDraggable(
    ref as React.MutableRefObject<HTMLDivElement>
  );
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !ref.current) return;

      const movedX = Math.abs(e.clientX - startPosRef.current.x);
      const movedY = Math.abs(e.clientY - startPosRef.current.y);
      const hasMovedPastThreshold =
        movedX > DRAG_THRESHOLD_PX || movedY > DRAG_THRESHOLD_PX;

      // 클릭(미세 움직임)과 드래그를 구분. threshold 전에 pointer-events:none 하면 탭 클릭이 막힘
      if (hasMovedPastThreshold) {
        ref.current.classList.add('dragging');
      }
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    ref.current?.classList.remove('dragging');
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      startPosRef.current = { x: e.clientX, y: e.clientY };
      setIsDragging(true);
      events.onMouseDown?.(e);
    },
    [events]
  );

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return (
    <S.DraggableScroller
      className={`${className || ''} ${isDragging ? 'grabbing' : ''}`}
    >
      <S.List
        className="draggable-scroller"
        {...events}
        ref={ref}
        onMouseDown={handleMouseDown}
      >
        {children}
      </S.List>
    </S.DraggableScroller>
  );
};

export default DraggableScroller;
